import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { List } from 'immutable';
import { pick } from 'lodash';
import { Observable, Subject } from 'rxjs';
import {
  IError,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
} from '../entity-data/entity-data.data';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { GlobalBannerTypesEnum } from '../shared/components/global-banner/global-banner.enum';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { IRouter } from '../shared/interfaces/router.interface';
import { TrackedTime } from '../tracked-time-data/models/tracked-time.model';
import {
  DEFAULT_SEARCH_PARAMS,
  TrackedTimeFiltersEnum,
} from '../tracked-time-data/tracked-time-data.data';
import { TrackedTimeDataDispatcher } from '../tracked-time-data/tracked-time-data.dispatcher';
import { TrackedTimeDataStore } from '../tracked-time-data/tracked-time-data.store';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import {
  ITrackingObject,
  TrackingButtonIdentifierEnum,
  TrackingInputTypeValuesEnum,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { TrackedTimeMasterBuilder } from './tracked-time-master.builder';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { TRACKED_TIMES_MASTER_ROUTE } from '../main/main.data';
import { NavigationService } from '../navigation/navigation.service';
import {
  TRACKED_TIMES_BILLED_ROUTE,
  TRACKED_TIMES_UNBILLED_ROUTE,
} from '../tracked-times/tracked-times.constant';

@Component({
  selector: 'app-tracked-time-master',
  templateUrl: 'tracked-time-master.component.html',
  styleUrls: ['tracked-time-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TrackedTimeMasterBuilder],
})
export class TrackedTimeMasterComponent implements OnInit, OnDestroy {
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _errorBannerId: string;

  primaryAction$: Observable<IRouter>;
  filters: List<IRouter>;
  featureName: string;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject: ITrackingObject;
  defaultSearchParams$: Observable<ISearchParams>;
  searchQuery$: Observable<string>;
  activeFilter$: Observable<string>;

  constructor(
    private _route: ActivatedRoute,
    private _trackedTimeMasterBuilder: TrackedTimeMasterBuilder,
    private _trackedTimeDataStore: TrackedTimeDataStore,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService
  ) {}

  ngOnInit() {
    const urlQuery = this._route.snapshot.queryParamMap.get(
      SearchParamsEnum.query
    );

    this.featureName = Toggles.KEYS.TIME_TRACKING;
    this.trackingScreenName = TrackingScreenNamesEnum.tracked_time_list;
    this.trackingObject = this._trackingObjectBuilder.buildInputList(
      TrackingInputTypeValuesEnum.tracked_time
    );
    this.filters = this._trackedTimeMasterBuilder.buildFilterRoutes(
      this.trackingObject,
      this.trackingScreenName
    );

    this.searchQuery$ = this._route.queryParamMap.pipe(
      takeUntil(this._destroyed),
      map((params: ParamMap) => params.get(SearchParamsEnum.query) || null),
      startWith(urlQuery || null)
    );

    this.primaryAction$ = this._trackedTimeDataStore.getDraftTrackedTime().pipe(
      takeUntil(this._destroyed),
      map((trackedTime: TrackedTime) =>
        this._trackedTimeMasterBuilder.buildPrimaryAction(
          this.trackingObject,
          this.trackingScreenName,
          TrackingButtonIdentifierEnum.add_input,
          !!trackedTime
        )
      ),
      startWith(
        this._trackedTimeMasterBuilder.buildPrimaryAction(
          this.trackingObject,
          this.trackingScreenName,
          TrackingButtonIdentifierEnum.add_input
        )
      )
    );

    this.defaultSearchParams$ = this._trackedTimeDataStore
      .getSearchParams(SEARCH_LIST_FILTER)
      .pipe(
        takeUntil(this._destroyed),
        startWith(DEFAULT_SEARCH_PARAMS),
        map((searchParams: ISearchParams) =>
          pick(searchParams, [
            SearchParamsEnum.sort_by,
            SearchParamsEnum.sort_order,
          ])
        )
      );

    this.activeFilter$ = this._navigationService.navigationEnds$.pipe(
      startWith(this._navigationService.getCurrentUrl()),
      map(() => {
        if (
          this._navigationService.isRouteActive([
            TRACKED_TIMES_MASTER_ROUTE,
            TRACKED_TIMES_UNBILLED_ROUTE,
          ])
        ) {
          return TrackedTimeFiltersEnum.unbilled;
        } else if (
          this._navigationService.isRouteActive([
            TRACKED_TIMES_MASTER_ROUTE,
            TRACKED_TIMES_BILLED_ROUTE,
          ])
        ) {
          return TrackedTimeFiltersEnum.billed;
        } else {
          return SEARCH_LIST_FILTER;
        }
      })
    );

    this._trackedTimeDataStore
      .getMasterError()
      .pipe(takeUntil(this._destroyed))
      .subscribe((masterError: IError) => {
        if (masterError) {
          const { error } = masterError;

          this._errorBannerId = this._globalBannerService.showBanner({
            type: GlobalBannerTypesEnum.error,
            message: error.message,
            dismissible: true,
            isSticky: true,
            onDismiss: () => {
              this._trackedTimeDataDispatcher.clearMasterError();

              this._errorBannerId = null;
            },
          });
        } else if (this._errorBannerId) {
          this._globalBannerService.hideBanner(this._errorBannerId);

          this._errorBannerId = null;
        }
      });
  }

  trackTime() {
    this._trackedTimeDataDispatcher.newDraftTrackedTime();
  }

  ngOnDestroy() {
    this._trackedTimeDataDispatcher.clearMasterError();

    if (this._errorBannerId) {
      this._globalBannerService.hideBanner(this._errorBannerId);
      this._errorBannerId = null;
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
